import { createChunkedMedia, type CreateChunkedMediaResponse } from '@/api/events';
import { createRender, editRender, type CreateRenderMediaPayload, type CreateRenderPayload } from '@/api/renders';
import { useAzureChunkUpload } from '@/composables/video-upload/useAzureChunkUpload';
import { useVideoUploadQueue, type VideoUploadJobUploaderType } from '@/stores/videoUploadQueue';
import type { UploaderFile } from '@/types/video-uploader';
import axios from 'axios';

export interface StartServiceVideoUploadPayload {
    eventId: number;
    eventExternalId: string | null;
    eventTitle: string;
    serviceSlug: string | null;
    uploaderType: VideoUploadJobUploaderType;
    eventStatus: number;
    introVideo: boolean;
    files: UploaderFile[];
    importedVideos: Readonly<Record<string, UploaderFile>>;
    startTimeNumber: number;
    endTimeNumber: number;
    calculateDuration: (file: UploaderFile) => Promise<number>;
}

type FileToSasMap = Record<string, CreateChunkedMediaResponse>;
type UploadCancelSource = ReturnType<typeof axios.CancelToken.source>;

const uploadCancelSources = new Map<string, UploadCancelSource>();

export function useServiceVideoUploadJob() {
    const uploadQueue = useVideoUploadQueue();

    function startServiceVideoUpload(payload: StartServiceVideoUploadPayload): string {
        const totalBytes = calculateTotalUploadSize(payload.files);
        const files = payload.files.map(file => ({
            id: file.id,
            name: file.name,
            size: file.size ?? 0,
            isImported: Boolean(payload.importedVideos[file.id]),
        }));
        const job = uploadQueue.startJob({
            eventId: payload.eventId,
            eventExternalId: payload.eventExternalId,
            eventTitle: payload.eventTitle,
            serviceSlug: payload.serviceSlug,
            uploaderType: payload.uploaderType,
            totalFiles: payload.files.length,
            totalBytes,
            files,
        });

        void runUploadJob(job.id, payload);

        return job.id;
    }

    async function submitServiceVideoRender(payload: StartServiceVideoUploadPayload): Promise<void> {
        const fileToSASMap: FileToSasMap = {};

        for (let index = 0; index < payload.files.length; index++) {
            const file = payload.files[index];

            if (!payload.importedVideos[file.id]) {
                throw new Error(`Local upload required for ${file.name}`);
            }

            fileToSASMap[file.id] = createImportedMediaInfo(file, index);
        }

        const renderPayload = await createServiceRenderPayload(payload, fileToSASMap);

        if (isEditUpload(payload)) {
            await editRender(renderPayload);
            return;
        }

        await createRender(renderPayload);
    }

    async function runUploadJob(jobId: string, payload: StartServiceVideoUploadPayload): Promise<void> {
        const fileToSASMap: FileToSasMap = {};
        const cancelSource = axios.CancelToken.source();
        let currentUploadingFileId: string | null = null;

        uploadCancelSources.set(jobId, cancelSource);

        try {
            uploadQueue.markUploading(jobId);

            const { uploadFileChunksParallel, commitBlockList } = useAzureChunkUpload({
                getSasUrl: (fileId: string) => {
                    const sasInfo = fileToSASMap[fileId];

                    if (!sasInfo?.sas) {
                        throw new Error('SAS URL is unavailable');
                    }

                    return sasInfo.sas;
                },
                onUploadedBytes: (bytes: number) => {
                    uploadQueue.addUploadedBytes(jobId, bytes);
                    if (currentUploadingFileId) {
                        uploadQueue.addFileUploadedBytes(jobId, currentUploadingFileId, bytes);
                    }
                },
                cancelToken: cancelSource.token,
                isCancelled: () => uploadCancelSources.get(jobId) !== cancelSource,
            });

            for (let index = 0; index < payload.files.length; index++) {
                throwIfCancelled(jobId, cancelSource);

                const file = payload.files[index];
                uploadQueue.updateCurrentFile(jobId, index + 1, file.name);

                if (payload.importedVideos[file.id]) {
                    uploadQueue.updateFileStatus(jobId, file.id, 'imported');
                    fileToSASMap[file.id] = createImportedMediaInfo(file, index);
                    continue;
                }

                currentUploadingFileId = file.id;
                uploadQueue.updateFileStatus(jobId, file.id, 'uploading');

                const sasInfo = await createChunkedMedia(payload.eventId, file.name, index);
                fileToSASMap[file.id] = sasInfo;

                if (!sasInfo.sas) {
                    throw new Error(`SAS URL unavailable for ${file.name}`);
                }

                const { blockIDs } = await uploadFileChunksParallel(file);
                await commitBlockList(sasInfo.sas, blockIDs, file.type || 'video/mp4');
                uploadQueue.updateFileStatus(jobId, file.id, 'uploaded');
                currentUploadingFileId = null;
            }

            throwIfCancelled(jobId, cancelSource);
            uploadQueue.markProcessing(jobId);

            const renderPayload = await createServiceRenderPayload(payload, fileToSASMap);

            throwIfCancelled(jobId, cancelSource);
            if (isEditUpload(payload)) {
                await editRender(renderPayload);
            } else {
                await createRender(renderPayload);
            }

            uploadQueue.markComplete(jobId);
        } catch (error) {
            if (axios.isCancel(error) || isUploadCancelledError(error)) {
                uploadQueue.markCancelled(jobId);
                return;
            }
            uploadQueue.markError(jobId, getErrorMessage(error));
        } finally {
            uploadCancelSources.delete(jobId);
        }
    }

    function cancelServiceVideoUpload(jobId: string): void {
        const cancelSource = uploadCancelSources.get(jobId);

        if (!cancelSource) {
            uploadQueue.markCancelled(jobId);
            return;
        }

        uploadCancelSources.delete(jobId);
        cancelSource.cancel('Video upload cancelled');
    }

    function throwIfCancelled(jobId: string, cancelSource: UploadCancelSource): void {
        if (uploadCancelSources.get(jobId) !== cancelSource) {
            throw new Error('Video upload cancelled');
        }
    }

    function isUploadCancelledError(error: unknown): boolean {
        return error instanceof Error && error.message === 'Video upload cancelled';
    }

    function createImportedMediaInfo(file: UploaderFile, order: number): CreateChunkedMediaResponse {
        if (!file.playbackUrl) {
            throw new Error(`Playback URL unavailable for ${file.name}`);
        }

        return {
            id: 0,
            url: file.playbackUrl,
            order,
        };
    }

    async function createServiceRenderPayload(
        payload: StartServiceVideoUploadPayload,
        fileToSASMap: FileToSasMap,
    ): Promise<CreateRenderPayload> {
        const trimData = await calculateStartStop(payload);
        const medias: CreateRenderMediaPayload[] = payload.files.map((file, index) => {
            const sasInfo = fileToSASMap[file.id];

            if (!sasInfo) {
                throw new Error(`Media record unavailable for ${file.name}`);
            }

            return {
                id: sasInfo.id || 0,
                name: file.name,
                sas: sasInfo.sas ?? null,
                order: index,
                mediaType: 1,
                url: file.playbackUrl ? sasInfo.url : (sasInfo.sas ?? sasInfo.url),
            };
        });

        let videoQueType = payload.introVideo ? 0 : 1;

        if (isEditUpload(payload)) {
            videoQueType = 8;

            if (payload.introVideo) {
                videoQueType = 7;

                if (trimData.start < 19) {
                    trimData.start = 19;
                }
            }
        }

        return {
            eventId: payload.eventId,
            medias,
            videoQueType,
            start: trimData.start,
            stop: trimData.stop,
        };
    }

    async function calculateStartStop(
        payload: StartServiceVideoUploadPayload,
    ): Promise<{ start: number; stop: number }> {
        let duration = 0;

        for (const file of payload.files) {
            duration += await payload.calculateDuration(file);
        }

        const endFile = payload.files[payload.files.length - 1];

        if (!endFile) {
            throw new Error('No video files selected');
        }

        const endVideoDuration = await payload.calculateDuration(endFile);
        const safeStart = Math.max(0, payload.startTimeNumber);
        const safeEnd = Math.min(endVideoDuration, Math.max(0, payload.endTimeNumber));
        const stop = duration - (endVideoDuration - safeEnd);

        return {
            start: safeStart,
            stop: Math.max(safeStart, stop),
        };
    }

    function calculateTotalUploadSize(files: UploaderFile[]): number {
        return files.filter(file => !file.playbackUrl && file.size).reduce((sum, file) => sum + (file.size ?? 0), 0);
    }

    function isEditUpload(payload: StartServiceVideoUploadPayload): boolean {
        return payload.eventStatus === 7 && payload.uploaderType === 'edit';
    }

    function getErrorMessage(error: unknown): string {
        return error instanceof Error ? error.message : 'Video upload failed';
    }

    return {
        startServiceVideoUpload,
        submitServiceVideoRender,
        cancelServiceVideoUpload,
    };
}
